import NoData from '@/components/common/NoData'
import ArrowDownIcon from '@/components/icons/ArrowDownIcon'
import ArrowUpIcon from '@/components/icons/ArrowUpIcon'
import { SyncErrorIcon } from '@/components/icons'
import useCheckOverlay from '@/features/admin/hooks/locationInterface/useCheckOverlay'
import { useAuthContext } from '@/features/auth/context'
import useSearchFlowValveError from '@/features/config/hooks/hydraulic/flowValve/useSearchFlowValveError'
import useResetFlowValve from '@/features/config/hooks/hydraulic/flowValve/useResetFlowValve'
import useStationReset from '@/features/config/hooks/station/useStationReset'
import SoilPopupTable from '@/features/config/pages/Soil/SoilPopupTable'
import { useSearchStation } from '@/features/operations/hooks/station'
import { usePermission } from '@/hooks/permissions'
import { useTranslation } from '@/hooks/useTranslation'
import { useAppSelector } from '@/store/stores'
import { selectInterfacesByOrganization } from '@/store/selectors/UserSelector'
import FlowValveTable from './FlowValveTable'
import {
  Box,
  CircularProgress,
  Grid,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Tabs,
  Tab,
  IconButton,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { TabContext, TabPanel } from '@mui/lab'
import { useMemo, useState } from 'react'
import classes from './style.module.scss'

import useSearchHydraulic from '@features/config/hooks/hydraulic/useSearchHydraulic'

const CIS_DARK_GREEN = '#064e3b'
const CIS_DARK_GREEN_HOVER = '#065f46'
const CIS_DARK_GREEN_BORDER = '#065f46'
const CIS_DARK_GREEN_TEXT = '#ecfdf5'

interface HistorySensorModalProps {
  data?: any
  dataFlowValve?: any
  interfaceShowing?: number | null
  onClose?: () => void
}

// Bright amber highlight for tabs that have at least 1 issue (high contrast on dark header)
const ERROR_HIGHLIGHT = '#FFB020'

// Per-tab sx: red-tinted pill + border when the tab has errors, so it stands out
const tabSx = (hasError: boolean) => ({
  ...(hasError && {
    color: `${ERROR_HIGHLIGHT} !important`,
    backgroundColor: 'rgba(255, 66, 74, 0.18)',
    border: '1px solid rgba(255, 66, 74, 0.6)',
    borderRadius: '6px',
    '&.Mui-selected': {
      color: `${ERROR_HIGHLIGHT} !important`,
      backgroundColor: 'rgba(255, 66, 74, 0.28)',
    },
  }),
})

// Tab label with a count badge when there is at least 1 issue
const renderTabLabel = (label: string, count: number) => (
  <Stack direction="row" alignItems="center" spacing={1}>
    <Box component="span" sx={{ fontWeight: 'inherit' }}>
      {label}
    </Box>
    {count > 0 && (
      <Box
        component="span"
        sx={{
          minWidth: 22,
          height: 22,
          px: 0.75,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '11px',
          bgcolor: '#FF424A',
          color: '#ffffff',
          fontSize: '13px',
          fontWeight: 800,
          lineHeight: 1,
        }}
      >
        {count}
      </Box>
    )}
  </Stack>
)

const NotificationModal = (props: HistorySensorModalProps) => {
  const { t } = useTranslation()
  const { onClose } = props
  const { mutate: resetAllStations, isLoading: isResettingStations } =
    useStationReset()
  const { mutate: resetFlowValve, isLoading: isResettingFlowValve } =
    useResetFlowValve()

  const { interfaceId } = useAuthContext()
  const { isViewOnly } = usePermission()
  const accessibleInterfaces = useAppSelector(selectInterfacesByOrganization)

  const activeInterfaces = useMemo(
    () => accessibleInterfaces.filter((it: any) => it.status),
    [accessibleInterfaces],
  )

  const [selectedInterfaceId, setSelectedInterfaceId] = useState<number>(
    Number(props.interfaceShowing || interfaceId || 0),
  )

  const handleChangeInterface = (event: SelectChangeEvent<number>) => {
    setSelectedInterfaceId(Number(event.target.value))
  }

  const { data: stationData } = useSearchStation(
    { PageSize: 0 },
    selectedInterfaceId,
  )
  const { data: flowValveErrorData } = useSearchFlowValveError(
    selectedInterfaceId,
    { PageSize: 0 },
  )
  const { data: errorOverlay } = useCheckOverlay(selectedInterfaceId)

  const listErStation = useMemo(() => {
    const list = stationData?.list || props.data || []
    if (errorOverlay === true) {
      return list.filter((s: any) => s.errorCode !== 0)
    }
    return []
  }, [stationData, errorOverlay, props.data])

  const displayFlowValve = flowValveErrorData || props.dataFlowValve

  const { data: pipeData } = useSearchHydraulic(
    { PageSize: 0, PageIndex: 1 },
    selectedInterfaceId,
  )

  // Handlers
  const handleResetStations = () => {
    if (isViewOnly) return
    resetAllStations({ interfaceId: selectedInterfaceId })
  }
  const handleResetFlow = () => {
    if (isViewOnly) return
    resetFlowValve(selectedInterfaceId)
  }

  // Tabs: 'station' | 'hydraulic'
  const [tab, setTab] = useState<'station' | 'hydraulic'>('station')
  const handleChangeTab = (
    _e: React.SyntheticEvent,
    value: 'station' | 'hydraulic',
  ) => setTab(value)

  // Badge counts must follow the same live, selected-interface data the tab
  // bodies render (listErStation / displayFlowValve), not the static props
  // passed once by ErrorPage - otherwise switching interfaces in the Select
  // leaves the tab badges frozen on the original interface's counts.
  const stationErrorCount = listErStation.length
  const hydraulicErrorCount = displayFlowValve?.list?.length ?? 0

  // Nice titles
  const stationsTitle = useMemo(
    () => t('errorsNotificationsModalStations'),
    [t],
  )
  const flowValveTitle = useMemo(
    () => t('errorsNotificationsModalFlowValve'),
    [t],
  )

  const flattenPipeTree = (pipe: any, level = 0): any[] => {
    const current = { ...pipe, level }
    const children =
      pipe.subPipes?.flatMap((sub: any) => flattenPipeTree(sub, level + 1)) ||
      []
    return [current, ...children]
  }

  return (
    <Stack className={classes.wrapperModal}>
      <form>
        {/* Merged header: title (left) + tabs (same row) + close (right) */}
        <Stack
          className={classes.header}
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={2}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={3}
            sx={{ minWidth: 0 }}
          >
            <Typography
              variant="h5"
              sx={{
                fontWeight: 800,
                fontSize: '1.6rem',
                color: '#ffffff',
                whiteSpace: 'nowrap',
              }}
            >
              {t('errorSummaryTitle')}
            </Typography>
            <Tabs
              value={tab}
              onChange={handleChangeTab}
              textColor="inherit"
              sx={{
                minHeight: '40px',
                gap: 1,
                '.MuiTab-root': {
                  minHeight: '40px',
                  px: 2,
                  fontWeight: 500,
                  fontSize: '16px',
                  textTransform: 'none',
                  color: '#C9D1D9',
                  border: '1px solid rgba(255,255,255,0.18)',
                  borderRadius: '6px',
                  transition: 'all 0.15s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.06)',
                  },
                  '&.Mui-selected': {
                    color: '#FFFFFF',
                    backgroundColor: 'rgba(255,255,255,0.16)',
                    borderColor: 'rgba(255,255,255,0.5)',
                    fontWeight: 700,
                  },
                },
                '.MuiTabs-indicator': { display: 'none' },
              }}
            >
              <Tab
                value="station"
                sx={tabSx(stationErrorCount > 0)}
                label={renderTabLabel(t('stationError'), stationErrorCount)}
              />
              <Tab
                value="hydraulic"
                sx={tabSx(hydraulicErrorCount > 0)}
                label={renderTabLabel(t('hydraulicError'), hydraulicErrorCount)}
              />
            </Tabs>
            {props.interfaceShowing && activeInterfaces.length > 1 && (
              <Select
                value={selectedInterfaceId}
                onChange={handleChangeInterface}
                size="small"
                IconComponent={() => (
                  <Stack sx={{ mr: '8px', gap: '1px', pointerEvents: 'none' }}>
                    <ArrowUpIcon sx={{ fontSize: '12px', color: '#fff' }} />
                    <ArrowDownIcon sx={{ fontSize: '12px', color: '#fff' }} />
                  </Stack>
                )}
                sx={{
                  color: '#fff',
                  fontSize: '13px',
                  height: '32px',
                  minWidth: '200px',
                  bgcolor: 'rgba(255,255,255,0.08)',
                  borderRadius: '6px',
                  '.MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255,255,255,0.2)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255,255,255,0.5)',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255,255,255,0.7)',
                  },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      bgcolor: '#1B2A3B',
                      border: '1px solid rgba(255,255,255,0.12)',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
                      '.MuiMenuItem-root': {
                        color: '#e0e0e0',
                        fontSize: '13px',
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
                        '&.Mui-selected': {
                          bgcolor: 'rgba(255,255,255,0.14)',
                          color: '#fff',
                          fontWeight: 600,
                        },
                        '&.Mui-selected:hover': {
                          bgcolor: 'rgba(255,255,255,0.18)',
                        },
                      },
                    },
                  },
                }}
              >
                {activeInterfaces.map((iface: any) => (
                  <MenuItem key={iface.id} value={iface.id}>
                    {iface.interfaceCustomName ||
                      iface.interfaceName ||
                      iface.name}
                  </MenuItem>
                ))}
              </Select>
            )}
          </Stack>

          {onClose && (
            <IconButton
              onClick={onClose}
              aria-label="close"
              sx={{ color: '#ffffff' }}
            >
              <CloseIcon />
            </IconButton>
          )}
        </Stack>

        {/* Body: responsive grid of widgets */}
        <Stack className={classes.body} sx={{ px: 2, pb: 2 }}>
          <TabContext value={tab}>
            <TabPanel value="station" sx={{ p: 0 }}>
              <Grid container spacing={2}>
                {/* Stations widget */}
                <Grid item xs={12} mt={2}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: 'rgba(255,255,255,0.04)',
                  border: '1px solid',
                  borderColor: 'divider',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  minWidth: 0,
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mb: 1.5 }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {stationsTitle}
                  </Typography>
                  <Stack
                    spacing={1}
                    direction="row"
                    className={classes.txtErTable}
                    onClick={
                      !isResettingStations && !isViewOnly
                        ? handleResetStations
                        : undefined
                    }
                    sx={{
                      bgcolor: CIS_DARK_GREEN,
                      color: CIS_DARK_GREEN_TEXT,
                      px: 1.5,
                      py: 0.75,
                      borderRadius: 1.5,
                      border: `1px solid ${CIS_DARK_GREEN_BORDER}`,
                      boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
                      alignItems: 'center',
                      userSelect: 'none',
                      '&:hover': { bgcolor: CIS_DARK_GREEN_HOVER },
                      opacity: isResettingStations || isViewOnly ? 0.5 : 1,
                      cursor:
                        isResettingStations || isViewOnly
                          ? 'not-allowed'
                          : 'pointer',
                      pointerEvents:
                        isResettingStations || isViewOnly ? 'none' : 'auto',
                    }}
                  >
                    <Box
                      component="span"
                      className={classes.txtBox}
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        fontWeight: 600,
                        letterSpacing: 0.2,
                      }}
                    >
                      <SyncErrorIcon sx={{ mr: 1, color: 'inherit' }} />
                      {t('errorsNotificationsModalAcknowledgeAll')}
                    </Box>
                  </Stack>
                </Stack>

                {isResettingStations ? (
                  <Stack
                    width="100%"
                    height="200px"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <CircularProgress />
                  </Stack>
                ) : (
                  <>
                    {listErStation.length > 0 ? (
                      <Box
                        sx={{ width: '100%', minWidth: 0, overflowX: 'auto' }}
                      >
                        <SoilPopupTable rows={listErStation} />
                      </Box>
                    ) : (
                      <Stack width="100%">
                        <Stack className={classes.bodyError}>
                          <NoData
                            text={t('errorsNotificationsModalNoErrorStations')}
                          />
                        </Stack>
                      </Stack>
                    )}
                  </>
                )}
              </Paper>
                </Grid>
              </Grid>
            </TabPanel>

            <TabPanel value="hydraulic" sx={{ p: 0 }}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              sx={{ pt: 2, width: '100%' }}
            >
              {/* Pipe status widget */}
              <Box sx={{ flex: '0 0 42%', minWidth: 0 }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: 'rgba(255,255,255,0.04)',
                    border: '1px solid',
                    borderColor: 'divider',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    minWidth: 0,
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ mb: 1.5 }}
                  >
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 600,
                        textTransform: 'uppercase',
                      }}
                    >
                      {t('errorsNotificationsModalPipeStatus')}
                    </Typography>
                    <Stack
                      spacing={1}
                      direction="row"
                      className={classes.txtErTable}
                      onClick={
                        !isResettingFlowValve && !isViewOnly
                          ? handleResetFlow
                          : undefined
                      }
                      sx={{
                        bgcolor: CIS_DARK_GREEN,
                        color: CIS_DARK_GREEN_TEXT,
                        px: 1.5,
                        py: 0.75,
                        borderRadius: 1.5,
                        border: `1px solid ${CIS_DARK_GREEN_BORDER}`,
                        boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
                        alignItems: 'center',
                        userSelect: 'none',
                        '&:hover': { bgcolor: CIS_DARK_GREEN_HOVER },
                        opacity: isResettingFlowValve || isViewOnly ? 0.5 : 1,
                        cursor:
                          isResettingFlowValve || isViewOnly
                            ? 'not-allowed'
                            : 'pointer',
                        pointerEvents:
                          isResettingFlowValve || isViewOnly ? 'none' : 'auto',
                      }}
                    >
                      <Box
                        component="span"
                        className={classes.txtBox}
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          fontWeight: 600,
                          letterSpacing: 0.2,
                        }}
                      >
                        <SyncErrorIcon sx={{ mr: 1, color: 'inherit' }} />
                        {t('errorsNotificationsModalAcknowledgeAll')}
                      </Box>
                    </Stack>
                  </Stack>
                  <TableContainer component={Paper} elevation={0}>
                    <Table sx={{ width: '100%' }} size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>{t('headers.pipeName')}</TableCell>
                          <TableCell>{t('commonLabelsStatus')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody
                        sx={{
                          backgroundColor: 'common.white',
                          '& .MuiTableRow-root:nth-of-type(even)': {
                            backgroundColor: '#F9F9F9',
                          },
                        }}
                      >
                        {pipeData?.list
                          ?.flatMap((pipe: any) => flattenPipeTree(pipe))
                          .map((row: any) => {
                            const statusColor =
                              row.status === 'Unexpected Flow'
                                ? '#dc2626'
                                : row.status === 'Ok'
                                ? '#16a34a'
                                : '#a1a1aa'
                            return (
                              <TableRow key={row.id}>
                                <TableCell>
                                  <Box sx={{ pl: row.level * 2 }}>
                                    {row.description}
                                  </Box>
                                </TableCell>
                                <TableCell
                                  sx={{
                                    color: statusColor,
                                    fontWeight: 600,
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {row.status || '-'}
                                </TableCell>
                              </TableRow>
                            )
                          })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Box>

              {/* Flow valve widget */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: 'rgba(255,255,255,0.04)',
                    border: '1px solid',
                    borderColor: 'divider',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    minWidth: 0,
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ mb: 1.5 }}
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {flowValveTitle}
                    </Typography>
                    <Stack
                      spacing={1}
                      direction="row"
                      className={classes.txtErTable}
                      onClick={
                        !isResettingFlowValve && !isViewOnly
                          ? handleResetFlow
                          : undefined
                      }
                      sx={{
                        bgcolor: CIS_DARK_GREEN,
                        color: CIS_DARK_GREEN_TEXT,
                        px: 1.5,
                        py: 0.75,
                        borderRadius: 1.5,
                        border: `1px solid ${CIS_DARK_GREEN_BORDER}`,
                        boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
                        alignItems: 'center',
                        userSelect: 'none',
                        '&:hover': { bgcolor: CIS_DARK_GREEN_HOVER },
                        opacity: isResettingFlowValve || isViewOnly ? 0.5 : 1,
                        cursor:
                          isResettingFlowValve || isViewOnly
                            ? 'not-allowed'
                            : 'pointer',
                        pointerEvents:
                          isResettingFlowValve || isViewOnly ? 'none' : 'auto',
                      }}
                    >
                      <Box
                        component="span"
                        className={classes.txtBox}
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          fontWeight: 600,
                          letterSpacing: 0.2,
                        }}
                      >
                        <SyncErrorIcon sx={{ mr: 1, color: 'inherit' }} />
                        {t('errorsNotificationsModalAcknowledgeAll')}
                      </Box>
                    </Stack>
                  </Stack>

                  {isResettingFlowValve ? (
                    <Stack
                      width="100%"
                      height="200px"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <CircularProgress />
                    </Stack>
                  ) : (
                    <>
                      {displayFlowValve?.list?.length > 0 ? (
                        <Box
                          sx={{ width: '100%', minWidth: 0, overflowX: 'auto' }}
                        >
                          <FlowValveTable rows={displayFlowValve.list} />
                        </Box>
                      ) : (
                        <Stack width="100%">
                          <Stack className={classes.bodyError}>
                            <NoData
                              text={t(
                                'errorsNotificationsModalNoErrorFlowValve',
                              )}
                            />
                          </Stack>
                        </Stack>
                      )}
                    </>
                  )}
                </Paper>
              </Box>
            </Stack>
            </TabPanel>
          </TabContext>
        </Stack>
      </form>
    </Stack>
  )
}

export default NotificationModal
